import { useEffect, useState } from "react"
import { getCase } from "../nira2/api"
import { Button } from "../nira2/ds"
import { WseCard } from "../nira2/answer/WorkspaceEntry"
import "../nira2/answer/answer-v4.css"
import { MatterPicker } from "../nira2/matter/MatterPicker"
import { WorkspaceCard } from "../nira2/matter/WorkspaceCard"
import type { SimEngine } from "./engine"

/**
 * The workspace card for one run-through draft, shared by the decisions and draft pages. The draft's
 * case is looked up for its name (a merged case opens the workspace it was folded into, as SimStart
 * does). A 404 means the workspace is gone: the draft reads as unattached. Any other failure keeps the
 * draft attached with the name unavailable, so the card never says "Add to workspace" for a draft
 * that has a case.
 */
export function DraftMatter({ kind, threadId, caseId, placement }: { kind: SimEngine["kind"]; threadId: string; caseId: string | null; placement: "float" | "rail" }) {
  const [state, setState] = useState<{ ready: boolean; matter: { id: string; title: string } | null }>({ ready: !caseId, matter: null })
  useEffect(() => {
    setState({ ready: !caseId, matter: null })
    if (!caseId) return
    let live = true
    getCase(caseId)
      .then((c) => { if (live) setState({ ready: true, matter: { id: c.merged_into || c.id, title: c.title } }) })
      .catch((err: unknown) => {
        if (!live) return
        const gone = (err as { status?: number }).status === 404
        setState({ ready: true, matter: gone ? null : { id: caseId, title: "name unavailable" } })
      })
    return () => { live = false }
  }, [caseId])
  const [picking, setPicking] = useState(false)
  // The decisions page's right rail: nothing when the draft already sits in a workspace, else one quiet line.
  if (placement === "rail") {
    if (!state.ready || state.matter) return null
    return (
      <div className="na4 nsd-ws">
        <WseCard title="Keep this in a workspace" text="A workspace is a folder for one client matter. It keeps this order draft, the notice, the replies and the files for that case together, so you can come back to it and carry on later.">
          <span className="wse-pick">
            <Button variant="primary" size="sm" aria-expanded={picking} onClick={() => setPicking((v) => !v)}>Add to workspace</Button>
            {picking ? (
              <div className="wse-pop">
                <MatterPicker type={kind} sessionId={threadId} currentCaseId={null}
                  onDone={(m) => { setState({ ready: true, matter: m }); setPicking(false) }} onClose={() => setPicking(false)} />
              </div>
            ) : null}
          </span>
        </WseCard>
      </div>
    )
  }
  return state.ready ? <WorkspaceCard type={kind} sessionId={threadId} matter={state.matter} placement={placement} onChange={(matter) => setState({ ready: true, matter })} /> : null
}
